import { ComboField, DateField } from '~/components/form';
import { FilterDrawer } from '~/components/ui';
import { DIGITAL_ASSISTANT_STATUS_OPTIONS } from '~/lib/ai/digitalAssistant';

/** The applied filters by URL key; an empty string means "no filter". */
type DigitalAssistantFilters = Record<string, string>;

/** The URL keys the drawer writes. */
export const ASSISTANT_FILTER_KEYS = [
  'status',
  'model',
  'agent',
  'createdFrom',
  'createdTo',
  'updatedFrom',
  'updatedTo',
] as const;

interface FilterOption {
  label: string;
  value: string;
}

interface DigitalAssistantFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  value: DigitalAssistantFilters;
  agents: FilterOption[];
  models: FilterOption[];
  onApply: (filters: DigitalAssistantFilters) => void;
  onClear: () => void;
}

const STATUS_OPTIONS: FilterOption[] = DIGITAL_ASSISTANT_STATUS_OPTIONS;

const splitValues = (value: string): string[] => (value ? value.split(',') : []);

/** Filters for the digital-assistant list: status, model, agent and created/updated date ranges, applied together. */
export default function DigitalAssistantFilterDrawer({
  open,
  onClose,
  value,
  agents,
  models,
  onApply,
  onClear,
}: DigitalAssistantFilterDrawerProps) {
  return (
    <FilterDrawer<DigitalAssistantFilters>
      open={open}
      onClose={onClose}
      value={value}
      onApply={onApply}
      onClear={onClear}
      testId="ai-digital-assistants-filter-drawer"
    >
      {(draft, patch) => (
        <>
          <ComboField
            id="digital-assistant-filter-status"
            testId="ai-digital-assistants-filter-status"
            label="Status"
            isMulti
            value={splitValues(draft.status)}
            options={STATUS_OPTIONS}
            onChange={(status) => patch({ status: status.join(',') })}
          />
          {/* Tags are not saved yet, so this filter has nothing to match. It is kept for parity. */}
          <ComboField
            id="digital-assistant-filter-tags"
            testId="ai-digital-assistants-filter-tags"
            label="Tags"
            isMulti
            value={[]}
            options={[]}
            placeholder="No tags yet"
            onChange={() => undefined}
          />
          <ComboField
            id="digital-assistant-filter-model"
            testId="ai-digital-assistants-filter-model"
            label="AI model"
            value={draft.model}
            options={[{ value: '', label: 'All models' }, ...models]}
            searchable
            onChange={(model) => patch({ model })}
          />
          <ComboField
            id="digital-assistant-filter-agent"
            testId="ai-digital-assistants-filter-agent"
            label="Agent"
            value={draft.agent}
            options={[{ value: '', label: 'All agents' }, ...agents]}
            searchable
            onChange={(agent) => patch({ agent })}
          />
          <div className="grid grid-cols-2 gap-4">
            <DateField
              id="digital-assistant-filter-created-from"
              testId="ai-digital-assistants-filter-created-from"
              label="Created from"
              value={draft.createdFrom}
              onChange={(createdFrom) => patch({ createdFrom })}
            />
            <DateField
              id="digital-assistant-filter-created-to"
              testId="ai-digital-assistants-filter-created-to"
              label="Created to"
              value={draft.createdTo}
              onChange={(createdTo) => patch({ createdTo })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateField
              id="digital-assistant-filter-updated-from"
              testId="ai-digital-assistants-filter-updated-from"
              label="Updated from"
              value={draft.updatedFrom}
              onChange={(updatedFrom) => patch({ updatedFrom })}
            />
            <DateField
              id="digital-assistant-filter-updated-to"
              testId="ai-digital-assistants-filter-updated-to"
              label="Updated to"
              value={draft.updatedTo}
              onChange={(updatedTo) => patch({ updatedTo })}
            />
          </div>
        </>
      )}
    </FilterDrawer>
  );
}
